import { useState, useCallback } from 'react';
import { useFetch } from './useFetch';
import API_ROUTES from './apiRoutes';

interface Material {
  id: string;
  title: string;
  description: string;
  keywords: string[];
  thumbnailKey: string;
  thumbnailUrl?: string; // URL completa da thumbnail
  previewUrl: string;
  purchaseUrl: string;
  referenceIds?: string[]; // IDs de referência do produto
  templateId?: string;
  contentIds: string[];
  groups?: { name: string; contents: any[] }[];
  contentCount?: number; // Contagem real de conteúdos
  createdAt?: string;
  isFree?: boolean; // Se o material é gratuito
  index?: number;
}

interface CreateMaterialData {
  title: string;
  description: string;
  keywords: string[];
  thumbnailKey: string;
  previewUrl: string;
  purchaseUrl: string;
  referenceIds?: string[];
  templateId?: string;
  contentIds?: string[];
  groups?: { name: string; contentIds: string[] }[];
}

interface MaterialState {
  materials: Material[];
  isLoading: boolean;
  error: string | null;
  success: boolean;
}

export const useMaterialManager = () => {
  const [materialState, setMaterialState] = useState<MaterialState>({
    materials: [],
    isLoading: false,
    error: null,
    success: false,
  });

  const { fetchWithAuth } = useFetch();

  // Helper function para mapear material da API
  const mapMaterialFromApi = (material: any, contentCount: number = 0): Material => ({
    id: material.id,
    title: material.title,
    description: material.description,
    keywords: material.keywords || [],
    thumbnailKey: material.thumbnailKey,
    thumbnailUrl: material.thumbnailUrl,
    previewUrl: material.previewUrl,
    purchaseUrl: material.purchaseUrl,
    referenceIds: material.referenceIds || (material.referenceId ? [material.referenceId] : []),
    templateId: material.templateId,
    contentIds: material.contentIds || [],
    groups: material.groups || [],
    contentCount: contentCount,
    createdAt: material.createdAt,
    isFree: material.isFree || false,
    index: material.index,
  });

  const createMaterial = async (materialData: CreateMaterialData): Promise<Material | null> => {
    setMaterialState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      success: false,
    }));

    try {
             // Filtrar campos vazios para evitar erros de validação
             const cleanMaterialData: any = {
               title: materialData.title,
               description: materialData.description,
               keywords: materialData.keywords,
               thumbnailKey: materialData.thumbnailKey,
               previewUrl: materialData.previewUrl,
               purchaseUrl: materialData.purchaseUrl,
               referenceIds: materialData.referenceIds,
               contentIds: materialData.contentIds,
               groups: materialData.groups
             };

             // Só incluir templateId se for um UUID válido
             if (materialData.templateId && materialData.templateId.trim() !== '') {
               const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
               if (uuidRegex.test(materialData.templateId.trim())) {
                 cleanMaterialData.templateId = materialData.templateId.trim();
               }
             }

      const response = await fetchWithAuth(API_ROUTES.MATERIALS.create(), {
        method: 'POST',
        body: JSON.stringify(cleanMaterialData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao criar material: ${response.status} - ${errorText}`);
      }

      const newMaterialData = await response.json();

      // Mapear os campos da API para o formato esperado
      const newMaterial = mapMaterialFromApi({
        ...newMaterialData,
        title: materialData.title,
        description: materialData.description,
        keywords: materialData.keywords,
        thumbnailKey: materialData.thumbnailKey,
        previewUrl: materialData.previewUrl,
        purchaseUrl: materialData.purchaseUrl,
        referenceIds: materialData.referenceIds,
        templateId: materialData.templateId,
        contentIds: materialData.contentIds || [],
      });

      setMaterialState(prev => ({
        ...prev,
        materials: [...prev.materials, newMaterial],
        isLoading: false,
        success: true,
      }));

      return newMaterial;
    } catch (error) {
      setMaterialState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
      }));
      return null;
    }
  };

  const fetchMaterialById = useCallback(async (materialId: string): Promise<any> => {
    try {
      const response = await fetchWithAuth(API_ROUTES.MATERIALS.get(materialId));

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao buscar material: ${response.status} - ${errorText}`);
      }

      const materialData = await response.json();
      
      return materialData;
    } catch (error) {
      throw error;
    }
  }, [fetchWithAuth]);

  const fetchMaterials = useCallback(async (): Promise<void> => {
    setMaterialState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const response = await fetchWithAuth(API_ROUTES.MATERIALS.showcase());

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao buscar materiais: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      // A API retorna {materials: Array}, então extraímos o array
      const materialsData = data.materials || [];

      // Mapear os campos da API para o formato esperado
      const materials = materialsData.map((material: any) => 
        mapMaterialFromApi(material, 0) // Sem contagem para melhor performance
      );

      setMaterialState(prev => ({
        ...prev,
        materials: materials,
        isLoading: false,
      }));
    } catch (error) {
      setMaterialState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
      }));
    }
  }, [fetchWithAuth]);

  const resetMaterialState = () => {
    setMaterialState({
      materials: [],
      isLoading: false,
      error: null,
      success: false,
    });
  };

  const updateMaterial = useCallback(async (materialId: string, updateData: {
    title?: string;
    description?: string;
    keywords?: string[];
    introductionUrl?: string;
    purchaseUrl?: string;
    isFree?: boolean;
    referenceIds?: string[];
    templateId?: string | null;
    contentIds?: string[];
    groups?: { name: string; contentIds: string[] }[];
  }): Promise<any> => {
    try {
      const response = await fetchWithAuth(API_ROUTES.MATERIALS.update(materialId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(`Erro ao atualizar material: ${response.status} - ${responseText}`);
      }
 
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        result = { success: true };
      }
      
      return result;
    } catch (error) {
      throw error;
    }
  }, [fetchWithAuth]);

  const reorderMaterials = useCallback(async (orderedIds: { materialId: string; index: number }[]): Promise<void> => {
    try {
      const response = await fetchWithAuth(API_ROUTES.MATERIALS.reorder(), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ materials: orderedIds }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao reordenar materiais: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      throw error;
    }
  }, [fetchWithAuth]);

  return {
    createMaterial,
    fetchMaterials,
    fetchMaterialById,
    updateMaterial,
    reorderMaterials,
    materialState,
    resetMaterialState,
  };
};
